import { Button } from "@/components/ui/button";
import { MoreHorizontal } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { cn } from "@/lib/utils";
import type { PrepRecipeRow } from "./types";

interface PrepRecipeTileProps {
  item: PrepRecipeRow;
  onClick?: () => void;
}

function MetaCol({ label, value }: { label: string; value?: string }) {
  return (
    <div className="hidden w-40 shrink-0 flex-col gap-2 lg:flex">
      <span className="text-sm text-(--color-text-secondary)">{label}</span>
      <span className="text-sm font-medium text-(--color-text-primary)">{value || "—"}</span>
    </div>
  );
}

export function PrepRecipeTile({ item, onClick }: PrepRecipeTileProps) {
  const { t } = useLingui();

  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      className={cn(
        "flex items-center justify-between gap-3 rounded-[0.625rem] border border-(--color-border) bg-(--color-surface-muted) p-3 transition-colors",
        onClick && "cursor-pointer hover:bg-(--color-surface-muted)/60",
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex size-17 shrink-0 items-center justify-center overflow-hidden rounded-[0.625rem] border border-(--shell-border) bg-(--color-surface-content)">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt={item.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-(--color-text-secondary)">
              {t`IMG`}
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-1">
          <span className="truncate text-base font-medium text-(--color-text-primary)">{item.name}</span>
          <span className="truncate text-sm font-medium text-(--color-text-secondary)">
            {item.prepRecipeId.toUpperCase()}
          </span>
        </div>
      </div>

      <MetaCol
        label={t`Allergens:`}
        value={item.allergenCount > 0 ? String(item.allergenCount) : undefined}
      />
      <MetaCol label={t`Yield:`} value={item.yield} />
      <MetaCol label={t`Prep time:`} value={item.timeToPrep} />
      <MetaCol
        label={t`Ingredients:`}
        value={String(item.ingredientCount)}
      />

      <Button
        variant="outline"
        size="icon"
        className="size-11 shrink-0 rounded-[0.625rem] border-(--shell-border) bg-(--color-surface-content)"
        aria-label={t`More actions for ${item.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
      </Button>
    </div>
  );
}
